import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Breadcrumbs,
    Link,
    Card,
    CardContent,
    Avatar,
    Button,
    Box,
    Grid,
    TextField,
    Skeleton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
    Pagination,
    Chip,
    Stack,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    LinearProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Group from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import SettingsIcon from '@mui/icons-material/Settings';
import TeamPermissionsMatrix from './TeamPermissionsMatrix';

const ROLE_CHIPS = [
    { value: 'All', label: 'All', color: 'default' },
    { value: 'translator', label: 'Translator', color: 'primary' },
    { value: 'proofreader', label: 'Proofreader', color: 'success' },
    { value: 'manager', label: 'Manager', color: 'secondary' },
];

const ROLE_OPTIONS = [
    {
        value: 'manager',
        label: 'Manager',
        description: 'Can manage team members and oversee assignments.',
    },
    {
        value: 'translator',
        label: 'Translator',
        description: 'Can translate assigned projects and collaborate on tasks.',
    },
    {
        value: 'proofreader',
        label: 'Proofreader',
        description: 'Can review translations and provide feedback.',
    },
];

export default function TeamPage() {
    const token = useSelector((state) => state.auth.token);
    const [ownedTeam, setOwnedTeam] = useState([]);
    const [memberTeams, setMemberTeams] = useState([]);
    const [teamLimits, setTeamLimits] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogMode, setDialogMode] = useState('email');
    const [inviteEmail, setInviteEmail] = useState('');
    const [userID, setUserID] = useState('');
    const [selectedRole, setSelectedRole] = useState('translator');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [openDialog, setOpenDialog] = useState(false);
    const [page, setPage] = useState(1);
    const [roleFilter, setRoleFilter] = useState('All');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingTranslator, setEditingTranslator] = useState(null);
    const [editRole, setEditRole] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const rowsPerPage = 6;

    const location = useLocation();
    const navigate = useNavigate();
    const userId = token ? jwtDecode(token).id : null;

    // Generate dynamic breadcrumbs from the current pathname
    const breadcrumbs = location.pathname
        .split('/')
        .filter((x) => x)
        .map((path, idx, arr) => (
            <Link
                key={path + idx}
                color={idx === arr.length - 1 ? 'text.primary' : 'inherit'}
                onClick={() => navigate(`/${arr.slice(0, idx + 1).join('/')}`)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize', display: 'flex', alignItems: 'center' }}
            >
                {path}
            </Link>
        ));

    useEffect(() => {
        const fetchTeamData = async () => {
            try {
                const [ownedRes, memberRes, limitsRes] = await Promise.all([
                    axios.get('/api/team', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/team/member-teams', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/team/limits', { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                setOwnedTeam(Array.isArray(ownedRes.data) ? ownedRes.data : []);
                setMemberTeams(Array.isArray(memberRes.data) ? memberRes.data : []);
                setTeamLimits(limitsRes.data);
            } catch (err) {
                console.error('Error fetching team data:', err);
                setSnackbar({ open: true, message: 'Failed to load team data.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchTeamData();
    }, [token]);

    const getInitials = (firstName = '', lastName = '') =>
        `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();

    const normalizeRole = (role) => (role === 'lead' ? 'manager' : role || 'translator');

    const getRoleConfig = (role) => {
        const normalizedRole = normalizeRole(role);
        return ROLE_CHIPS.find((chip) => chip.value === normalizedRole) || ROLE_CHIPS[1];
    };

    // ---- FILTER, PAGINATION FOR OWNED TEAM ----
    const roleFiltered = ownedTeam.filter((t) =>
        roleFilter === 'All' ? true : normalizeRole(t.role) === roleFilter
    );

    const filtered = roleFiltered.filter((t) => {
        const name = `${t.userId.firstName || ''} ${t.userId.lastName || ''}`.toLowerCase();
        const email = (t.userId.email || '').toLowerCase();
        return (
            name.includes(searchQuery.toLowerCase()) ||
            email.includes(searchQuery.toLowerCase())
        );
    });

    const totalPages = Math.ceil(filtered.length / rowsPerPage);
    const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    // ---- HANDLERS FOR OWNED TEAM ----
    const handleRemove = async (translatorId) => {
        if (!window.confirm('Are you sure you want to remove this translator?')) return;
        try {
            await axios.delete(`/api/team/translators/${translatorId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setOwnedTeam((prev) => prev.filter((t) => t.userId._id !== translatorId));
            setSnackbar({ open: true, message: 'Translator removed.', severity: 'success' });
        } catch (err) {
            console.error('Error removing translator:', err);
            setSnackbar({ open: true, message: 'Failed to remove translator.', severity: 'error' });
        }
    };

    // Toggle only between active and inactive
    const handleToggleTranslatorStatus = async (translatorId) => {
        if (!window.confirm('Toggle this translator’s status?')) return;
        try {
            const { data } = await axios.put(
                `/api/team/translators/${translatorId}/status`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Expecting backend returns data.translator.status
            const updatedStatus = data.translator?.status;
            setOwnedTeam((prev) =>
                prev.map((t) =>
                    t.userId._id === translatorId ? { ...t, status: updatedStatus } : t
                )
            );
            setSnackbar({
                open: true,
                message: `Translator status set to ${updatedStatus}.`,
                severity: 'info',
            });
        } catch (err) {
            console.error('Error toggling translator status:', err);
            setSnackbar({ open: true, message: 'Failed to update status.', severity: 'error' });
        }
    };

    const handleEditMember = (translator) => {
        setEditingTranslator(translator);
        setEditRole(translator.role);
        setEditNotes(translator.managerNotes || '');
        setEditDialogOpen(true);
    };

    const handleSaveMemberChanges = async () => {
        try {
            await axios.put(
                `/api/team/translators/${editingTranslator.userId._id}`,
                { role: editRole, managerNotes: editNotes },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setOwnedTeam((prev) =>
                prev.map((t) =>
                    t.userId._id === editingTranslator.userId._id
                        ? { ...t, role: editRole, managerNotes: editNotes }
                        : t
                )
            );

            setEditDialogOpen(false);
            setSnackbar({ open: true, message: 'Member updated successfully.', severity: 'success' });
        } catch (err) {
            console.error('Error updating member:', err);
            setSnackbar({ open: true, message: 'Failed to update member.', severity: 'error' });
        }
    };

    const handleApproveInvite = async (teamId) => {
        try {
            await axios.put(
                `/api/team/member-teams/${teamId}/approve`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMemberTeams((prev) =>
                prev.map((team) =>
                    team._id === teamId ? { ...team, status: 'active' } : team
                )
            );
            setSnackbar({ open: true, message: 'Invitation approved.', severity: 'success' });
        } catch (err) {
            console.error('Error approving invite:', err);
            setSnackbar({ open: true, message: 'Failed to approve invite.', severity: 'error' });
        }
    };

    const handleInviteTranslator = async () => {
        // Frontend check for limits
        if (teamLimits && !teamLimits.isUnlimited && teamLimits.activeSeats >= teamLimits.teamLimit) {
            setSnackbar({
                open: true,
                message: `Team limit reached (${teamLimits.teamLimit} seats). Upgrade your plan to add more members.`,
                severity: 'warning'
            });
            return;
        }

        try {
            await axios.post(
                '/api/team/invite',
                { email: inviteEmail, role: selectedRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh logic - ideally re-fetch or optimistically update
            // Re-fetch limits to be safe
            const [ownedRes, limitsRes] = await Promise.all([
                axios.get('/api/team', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/team/limits', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setOwnedTeam(Array.isArray(ownedRes.data) ? ownedRes.data : []);
            setTeamLimits(limitsRes.data);

            setInviteEmail('');
            setSelectedRole('translator');
            setOpenDialog(false);
            setSnackbar({ open: true, message: 'Invitation sent.', severity: 'success' });
        } catch (err) {
            console.error('Error inviting translator:', err);
            const msg = err.response?.data?.msg || 'Failed to send invitation.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const handleAddUserById = async () => {
        try {
            await axios.post(
                '/api/team/add-user',
                { userId, role: selectedRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const res = await axios.get('/api/team', { headers: { Authorization: `Bearer ${token}` } });
            setOwnedTeam(Array.isArray(res.data) ? res.data : []);
            setUserID('');
            setSelectedRole('translator');
            setOpenDialog(false);
            setSnackbar({ open: true, message: 'User added to team.', severity: 'success' });
        } catch (err) {
            console.error('Error adding user by ID:', err);
            setSnackbar({ open: true, message: 'Failed to add user.', severity: 'error' });
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // ---- HANDLERS FOR MEMBER TEAMS ----
    const handleLeaveTeam = async (teamId) => {
        if (!window.confirm('Are you sure you want to leave this team?')) return;
        try {
            await axios.delete(`/api/team/member-teams/${teamId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMemberTeams((prev) => prev.filter((team) => team._id !== teamId));
            setSnackbar({ open: true, message: 'You have left the team.', severity: 'info' });
        } catch (err) {
            console.error('Error leaving team:', err);
            setSnackbar({ open: true, message: 'Failed to leave team.', severity: 'error' });
        }
    };

    // ---- ROLE CHIPS INLINE ----
    const renderRoleFilterChips = () => (
        <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
            {ROLE_CHIPS.map(({ value, label, color }) => (
                <Chip
                    key={value}
                    label={label}
                    clickable
                    color={roleFilter === value ? color : 'default'}
                    variant={roleFilter === value ? 'filled' : 'outlined'}
                    onClick={() => {
                        setRoleFilter(value);
                        setPage(1);
                    }}
                />
            ))}
        </Stack>
    );

    // ---- LOADING STATE ----
    if (loading) {
        return (
            <Container sx={{ mt: 6 }}>
                <Typography variant="h4" gutterBottom>
                    Team Management
                </Typography>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Grid item xs={12} md={6} lg={4} key={i}>
                            <Skeleton variant="rounded" height={300} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        );
    }

    // ---- MAIN RETURN ----
    return (
        <Container maxWidth="lg" sx={{ mt: 6 }}>
            {/* DYNAMIC BREADCRUMBS WITH GROUP ICON AND “Team” LABEL */}
            <Box sx={{ mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link
                        underline="hover"
                        color="inherit"
                        onClick={() => navigate('/team')}
                        sx={{
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Group sx={{ mr: 0.5, fontSize: '1rem' }} />
                        Team
                    </Link>
                    {breadcrumbs}
                </Breadcrumbs>
            </Box>

            <Typography variant="h4" gutterBottom>
                Team Management
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <ToggleButtonGroup
                    value={activeTab}
                    exclusive
                    onChange={(e, val) => val !== null && setActiveTab(val)}
                    aria-label="team tabs"
                    size="small"
                >
                    <ToggleButton value={0} sx={{ px: 3, textTransform: 'none' }}>
                        <Group sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Members
                    </ToggleButton>
                    <ToggleButton value={1} sx={{ px: 3, textTransform: 'none' }}>
                        <PersonAddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        External Teams
                    </ToggleButton>
                    {ownedTeam.length > 0 && (
                        <ToggleButton value={2} sx={{ px: 3, textTransform: 'none' }}>
                            <SettingsIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                            Settings
                        </ToggleButton>
                    )}
                </ToggleButtonGroup>
            </Box>

            {activeTab === 0 && (
                <>
                    {/* TEAM LIMITS / SEAT USAGE */}
                    {teamLimits && (
                        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Plan Usage: {teamLimits.planLabel}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ width: '100%', mr: 1 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                teamLimits.isUnlimited
                                                    ? 0
                                                    : Math.min((teamLimits.activeSeats / teamLimits.teamLimit) * 100, 100)
                                            }
                                            color={
                                                !teamLimits.isUnlimited &&
                                                    teamLimits.activeSeats >= teamLimits.teamLimit
                                                    ? 'error'
                                                    : 'primary'
                                            }
                                            sx={{ height: 10, borderRadius: 5 }}
                                        />
                                    </Box>
                                    <Box sx={{ minWidth: 35 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {teamLimits.activeSeats} /{' '}
                                            {teamLimits.isUnlimited ? '∞' : teamLimits.teamLimit}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {teamLimits.isUnlimited
                                        ? 'You have unlimited seats.'
                                        : `You have used ${teamLimits.activeSeats} of ${teamLimits.teamLimit} available seats.`}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

            {/* Top Card: Search, Role Filter Chips, Add Button */}
            <Box sx={{ mb: 3 }}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Search team members..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{ flex: 1, minWidth: 200 }}
                                    />
                                    {renderRoleFilterChips()}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => {
                                        setDialogMode('email');
                                        setSelectedRole('translator');
                                        setOpenDialog(true);
                                    }}
                                    size="small"
                                    sx={{
                                        borderRadius: '8px',
                                        backgroundColor: '#B5EAD7', // pastel green
                                        color: 'black',
                                        textTransform: 'none',
                                        px: 2,
                                        '&:hover': {
                                            backgroundColor: '#A0D5C2',
                                        },
                                    }}
                                >
                                    Add New
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>

            {/* List of Owned Team Translators */}
            {paginated.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                        sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            bgcolor: 'grey.200',
                            mb: 2,
                        }}
                    >
                        <PersonAddIcon fontSize="large" color="action" />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                        No team members found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Use the “Add New” button above to add team members.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => {
                            setDialogMode('email');
                            setSelectedRole('translator');
                            setOpenDialog(true);
                        }}
                        size="small"
                        sx={{
                            borderRadius: '8px',
                            backgroundColor: '#B5EAD7',
                            color: 'black',
                            textTransform: 'none',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#A0D5C2',
                            },
                        }}
                    >
                        Invite Team Member
                    </Button>
                </Box>
            ) : (
                <Grid container spacing={2} alignItems="stretch">
                    {paginated.map((t) => {
                        const statusLabel = t.status
                            ? t.status.charAt(0).toUpperCase() + t.status.slice(1)
                            : 'N/A';
                        const canToggle = t.status === 'active' || t.status === 'inactive';
                        const toggleLabel = t.status === 'active' ? 'Set Inactive' : 'Set Active';
                        const roleConfig = getRoleConfig(t.role);

                        return (
                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={4}
                                key={t.userId._id || t.userId.email}
                                sx={{ display: 'flex' }}
                            >
                                <Card
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            flexGrow: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                                            <Avatar
                                                src={
                                                    t.userId.avatarUrl
                                                        ? `/api/users/avatars/${t.userId._id}/${t.userId.avatarUrl
                                                            .split('/')
                                                            .pop()}`
                                                        : ''
                                                }
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    margin: '0 auto',
                                                    mb: 1,
                                                }}
                                            >
                                                {getInitials(t.userId.firstName, t.userId.lastName)}
                                            </Avatar>

                                            <Typography variant="h6" component="div">
                                                {t.userId.firstName || ''} {t.userId.lastName || ''}
                                            </Typography>

                                            <Chip
                                                label={roleConfig.label}
                                                color={roleConfig.color}
                                                size="small"
                                                sx={{ mt: 1, mb: 1 }}
                                            />

                                            {/* —— EMAIL + SOCIAL ICONS INLINE (no @status) —— */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 1,
                                                }}
                                            >
                                                {t.userId.linkedInProfile && (
                                                    <IconButton
                                                        component="a"
                                                        href={t.userId.linkedInProfile}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label="LinkedIn"
                                                        size="small"
                                                    >
                                                        <LinkedInIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {t.userId.twitter && (
                                                    <IconButton
                                                        component="a"
                                                        href={t.userId.twitter}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label="Twitter"
                                                        size="small"
                                                    >
                                                        <TwitterIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {t.userId.github && (
                                                    <IconButton
                                                        component="a"
                                                        href={t.userId.github}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label="GitHub"
                                                        size="small"
                                                    >
                                                        <GitHubIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {t.userId.instagram && (
                                                    <IconButton
                                                        component="a"
                                                        href={t.userId.instagram}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label="Instagram"
                                                        size="small"
                                                    >
                                                        <InstagramIcon fontSize="small" />
                                                    </IconButton>
                                                )}

                                                <Typography variant="body2" color="text.secondary">
                                                    <a
                                                        href={`mailto:${t.userId.email}`}
                                                        style={{ color: '#d63384', textDecoration: 'none' }}
                                                    >
                                                        {t.userId.email}
                                                    </a>
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* SMALL, CENTERED BUTTONS ABOVE STATS */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: 1,
                                                my: 1,
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => navigate(`/dashboard/chat/${t.userId._id}`)}
                                                sx={{
                                                    borderRadius: '6px',
                                                    backgroundColor: '#A8D0E6', // pastel blue
                                                    color: 'black',
                                                    textTransform: 'none',
                                                    py: 0.5,
                                                    px: 1,
                                                    fontSize: '0.7rem',
                                                    '&:hover': {
                                                        backgroundColor: '#91B8D3',
                                                    },
                                                }}
                                            >
                                                Message
                                            </Button>

                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleRemove(t.userId._id)}
                                                sx={{
                                                    borderRadius: '6px',
                                                    backgroundColor: '#FFB3BA', // pastel pink
                                                    color: 'black',
                                                    textTransform: 'none',
                                                    py: 0.5,
                                                    px: 1,
                                                    fontSize: '0.7rem',
                                                    '&:hover': {
                                                        backgroundColor: '#E69DA6',
                                                    },
                                                }}
                                            >
                                                Remove
                                            </Button>
                                            {canToggle && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleToggleTranslatorStatus(t.userId._id)}
                                                    sx={{
                                                        borderRadius: '6px',
                                                        backgroundColor: '#FFDAC1', // pastel orange
                                                        color: 'black',
                                                        textTransform: 'none',
                                                        py: 0.5,
                                                        px: 1,
                                                        fontSize: '0.7rem',
                                                        '&:hover': {
                                                            backgroundColor: '#E0B691',
                                                        },
                                                    }}
                                                >
                                                    {toggleLabel}
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleEditMember(t)}
                                                sx={{
                                                    borderRadius: '6px',
                                                    backgroundColor: '#E2F0CB', // pastel green-yellow
                                                    color: 'black',
                                                    textTransform: 'none',
                                                    py: 0.5,
                                                    px: 1,
                                                    fontSize: '0.7rem',
                                                    '&:hover': {
                                                        backgroundColor: '#CDDEB3',
                                                    },
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        </Box>

                                        {/* MEMBER DETAILS: Availability, Workload, Domains */}
                                        <Box sx={{ mt: 2, mb: 2 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                <strong>Availability:</strong> {t.userId.availability || 'Not set'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                <strong>Workload:</strong> {t.userId.workloadCapacity || 'Not set'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                <strong>Domains:</strong> {Array.isArray(t.userId.domains) ? t.userId.domains.join(', ') : (t.userId.domains || 'Not set')}
                                            </Typography>
                                            {t.managerNotes && (
                                                <Box sx={{ mt: 1, p: 1, bgcolor: '#fff9c4', borderRadius: '4px', borderLeft: '4px solid #fbc02d' }}>
                                                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                                        <strong>Notes:</strong> {t.managerNotes}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* STATS: Projects / Languages / Status */}
                                        <Grid container spacing={1} sx={{ mt: 1 }}>
                                            <Grid item xs={4}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6">
                                                        {t.assignedProjectsCount || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        Projects
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6">
                                                        {t.userId.translatedLanguages?.length || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        Languages
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: t.status === 'active' ? 'success.main' : 'warning.main',
                                                        }}
                                                    >
                                                        {statusLabel}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        Status
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {/* Performance Section */}
                                        {t.userId.performanceStats && (
                                            <Box sx={{ mt: 2 }}>
                                                <Divider sx={{ mb: 1 }} />
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="text.secondary">Rating: {t.userId.performanceStats.rating}/5</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', display: 'block' }}>Delivery: {t.userId.performanceStats.onTimeDelivery}%</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                    </Grid>
                )}

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, p) => setPage(p)}
                            color="primary"
                        />
                    </Box>
                )}
                </>
            )}

            {/* Teams You’re a Member Of */}
            {activeTab === 1 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        Teams You’re a Member Of
                    </Typography>
                {memberTeams.length === 0 ? (
                    <Typography>No team memberships yet.</Typography>
                ) : (
                    <Grid container spacing={2} alignItems="stretch">
                        {memberTeams.map((team) => {
                            const isActive = team.status === 'active';
                            const isPending = team.status === 'pending';
                            const statusLabel = team.status
                                ? team.status.charAt(0).toUpperCase() + team.status.slice(1)
                                : 'N/A';

                            // Derive owner name (ignore company field)
                            const ownerName =
                                `${team.owner.firstName || ''} ${team.owner.lastName || ''}`.trim() || 'N/A';

                            return (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={team._id}
                                    sx={{ display: 'flex' }}
                                >
                                    <Card
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <CardContent>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {team.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Owner: {ownerName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: isActive ? 'success.main' : 'warning.main',
                                                }}
                                            >
                                                {statusLabel}
                                            </Typography>
                                        </CardContent>

                                        <Box sx={{ p: 2, pt: 0, textAlign: 'center' }}>
                                            {isPending && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleApproveInvite(team._id)}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        backgroundColor: '#B5EAD7',
                                                        color: 'black',
                                                        textTransform: 'none',
                                                        px: 2,
                                                        '&:hover': {
                                                            backgroundColor: '#A0D5C2',
                                                        },
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                            )}

                                            {!isPending && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleToggleTranslatorStatus(team._id)}
                                                        sx={{
                                                            borderRadius: '6px',
                                                            backgroundColor: '#FFDAC1', // pastel orange
                                                            color: 'black',
                                                            textTransform: 'none',
                                                            py: 0.5,
                                                            px: 1,
                                                            fontSize: '0.7rem',
                                                            '&:hover': {
                                                                backgroundColor: '#E0B691',
                                                            },
                                                        }}
                                                    >
                                                        {isActive ? 'Set Inactive' : 'Set Active'}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleLeaveTeam(team._id)}
                                                        sx={{
                                                            borderRadius: '6px',
                                                            backgroundColor: '#FFB3BA', // pastel pink
                                                            color: 'black',
                                                            textTransform: 'none',
                                                            py: 0.5,
                                                            px: 1,
                                                            fontSize: '0.7rem',
                                                            '&:hover': {
                                                                backgroundColor: '#E69DA6',
                                                            },
                                                        }}
                                                    >
                                                        Leave Team
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
                </Box>
            )}

            {activeTab === 2 && (
                <TeamPermissionsMatrix token={token} />
            )}

            {/* Invite / Add Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6">Add a new team member</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose how you want to invite the person and assign their role.
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                    {teamLimits && (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.primary">
                                    Current Team Usage ({teamLimits.planLabel})
                                </Typography>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {teamLimits.activeSeats} /{' '}
                                    {teamLimits.isUnlimited ? '∞' : teamLimits.teamLimit} Members
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={
                                    teamLimits.isUnlimited
                                        ? 0
                                        : Math.min(
                                            (teamLimits.activeSeats / teamLimits.teamLimit) * 100,
                                            100
                                        )
                                }
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: 'grey.200',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: (theme) => {
                                            const usage = teamLimits.isUnlimited
                                                ? 0
                                                : teamLimits.activeSeats / teamLimits.teamLimit;
                                            return usage >= 1
                                                ? 'error.main'
                                                : usage >= 0.8
                                                    ? 'warning.main'
                                                    : 'success.main';
                                        },
                                    },
                                }}
                            />
                            {!teamLimits.isUnlimited &&
                                teamLimits.activeSeats >= teamLimits.teamLimit && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 1, display: 'block' }}
                                    >
                                        You have reached your team limit. Upgrade your plan to add more members.
                                    </Typography>
                                )}
                        </Box>
                    )}
                    <ToggleButtonGroup
                        value={dialogMode}
                        exclusive
                        onChange={(_, val) => val && setDialogMode(val)}
                        fullWidth
                        sx={{ mb: 3 }}
                    >
                        <ToggleButton value="email">Invite by Email</ToggleButton>
                        <ToggleButton value="id">Add by User ID</ToggleButton>
                    </ToggleButtonGroup>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {dialogMode === 'email' ? (
                            <TextField
                                label="Email address"
                                type="email"
                                fullWidth
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                error={inviteEmail && !/.+@.+\..+/.test(inviteEmail)}
                                helperText={
                                    inviteEmail && !/.+@.+\..+/.test(inviteEmail)
                                        ? 'Enter a valid email address'
                                        : 'We’ll send an invitation link to this email.'
                                }
                            />
                        ) : (
                            <TextField
                                label="User ID"
                                fullWidth
                                value={userID}
                                onChange={(e) => setUserID(e.target.value)}
                                helperText="Paste the user ID shared by your team member."
                            />
                        )}
                        <FormControl fullWidth>
                            <InputLabel id="team-role-select-label">Role</InputLabel>
                            <Select
                                labelId="team-role-select-label"
                                label="Role"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                            >
                                {ROLE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Box>
                                            <Typography variant="subtitle2">{option.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.description}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => {
                            setOpenDialog(false);
                            setDialogMode('email');
                            setSelectedRole('translator');
                        }}
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        disabled={
                            dialogMode === 'email'
                                ? !/.+@.+\..+/.test(inviteEmail)
                                : userID.trim() === ''
                        }
                        onClick={dialogMode === 'email' ? handleInviteTranslator : handleAddUserById}
                        sx={{
                            borderRadius: '8px',
                            backgroundColor: '#B5EAD7',
                            color: 'black',
                            textTransform: 'none',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#A0D5C2',
                            },
                        }}
                    >
                        {dialogMode === 'email' ? 'Send Invitation' : 'Add User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for feedback */}
            {/* Edit Member Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Team Member</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="edit-role-label">Role</InputLabel>
                            <Select
                                labelId="edit-role-label"
                                value={editRole}
                                label="Role"
                                onChange={(e) => setEditRole(e.target.value)}
                            >
                                {ROLE_OPTIONS.concat([
                                    { value: 'editor', label: 'Editor' },
                                    { value: 'qa', label: 'QA' },
                                    { value: 'lead', label: 'Lead' }
                                ]).map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Manager Notes"
                            multiline
                            rows={4}
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            placeholder="Add private notes about this team member..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveMemberChanges} variant="contained" color="primary">
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose}>
                <Alert
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    onClose={handleSnackbarClose}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
